/**
 * The rev4 widget contract's vocabulary (CT-5641).
 *
 * Every presentation type publishes a `configuration_schema` whose `bindings`
 * describe what the author must say, and the backend's sync turns those
 * bindings into the WIDGET query it derives from the selected DATASET. The
 * enums below are that schema's, read from the 29 type folders of
 * `widget_types_v2` and cross-checked against guides 12 and 18 of the
 * invocation set (2026-10-16).
 *
 * They are repeated here rather than read off the schema at runtime for the
 * two jobs a schema cannot do: naming a value in code (`AGGREGATE_WITHOUT_FIELD`
 * is a rule, not a list) and giving the builder something to render before a
 * type is chosen. Everything a type *varies* — which slots it has, which
 * aggregates each accepts, which data types fit — is read from its own schema
 * by `widgetSlotModel`, never from here.
 *
 * ## What rev4 changed, in one paragraph
 *
 * A binding used to be `{ field_key }` — a pointer into a query the backend
 * had already built from the entity, the groupers and the exposed columns. In
 * rev4 a binding is self-describing: `field` names a **column of the DATASET**
 * and `aggregate` / `time_grain` say what to do with it, so the sync can
 * *create* the WIDGET field instead of resolving a name that must already
 * exist. `field_key` stays, as the stable output key, and the pair is not a
 * duplication: `field: 'total_amount'` + `aggregate: 'SUM'` produces
 * `field_key: 'total_amount_sum'`, which is what the shaped response is keyed
 * on. Sending only `field_key` is still legal — the schema calls it "modo v1"
 * — and means the field already exists.
 */

/** `visualization_schema.contract_version`. All 29 types declare `const: 2`. */
export const WIDGET_CONTRACT_VERSION = 2;

/** `aggregate` on any slot whose role is `AGGREGATE`. */
export const WIDGET_AGGREGATES = Object.freeze([
  'SUM', 'AVG', 'MIN', 'MAX', 'COUNT', 'COUNT_DISTINCT', 'COUNT_ROWS',
]);

/**
 * The one aggregate that takes no `field`: it counts the rows of the route.
 * The schema enforces both halves — `COUNT_ROWS` with a `field` is rejected,
 * and any other aggregate without one is too.
 */
export const AGGREGATE_WITHOUT_FIELD = 'COUNT_ROWS';

/**
 * What `COUNT_ROWS` produces.
 *
 * Not a column's type but a result's: a count is an integer, which is why a
 * slot asking for `MONEY` refuses one however the author words it. Named here
 * because the check happens in two places — placing a measure in a slot, and
 * validating the binding that came of it.
 */
export const ROW_COUNT_DATA_TYPE = 'INTEGER';

/** `time_grain` on a DATE/TIMESTAMP dimension: the bucket it groups into. */
export const WIDGET_TIME_GRAINS = Object.freeze(['DAY', 'WEEK', 'MONTH', 'QUARTER', 'YEAR']);

/**
 * `date_part` — an extraction rather than a bucket, for the calendar views.
 * Only `HEATMAP`'s two axes declare it; a slot that does not is sent none.
 */
export const WIDGET_DATE_PARTS = Object.freeze([
  'HOUR_OF_DAY', 'DAY_OF_WEEK', 'DAY_OF_MONTH', 'MONTH_OF_YEAR', 'QUARTER_OF_YEAR',
]);

/**
 * `x-field-kinds`: what a slot accepts.
 *
 * - `AGGREGATE` — a measure; needs an `aggregate`.
 * - `DIMENSION` — a grouping column; must **not** carry one.
 * - `COLUMN` — a raw column of a record set; must not carry one either.
 *
 * A slot may declare two (`CORRELATION.x` is `COLUMN|AGGREGATE`), and there
 * the author's choice of aggregate is what decides the role.
 */
export const WIDGET_FIELD_ROLES = Object.freeze({
  AGGREGATE: 'AGGREGATE',
  DIMENSION: 'DIMENSION',
  COLUMN: 'COLUMN',
});

/**
 * Slots that bind no field, and so declare no `x-field-kinds`.
 *
 * They are part of `bindings` and travel in the same object, but each has its
 * own shape and its own meaning, so the generic field editor must leave them
 * alone: `time` delimits the period (guide 12 §6), `comparisons` asks for
 * extra executions of the same query, and the last four are provider or
 * context descriptors that no public authoring API fills yet (guide 18 §2).
 */
export const NON_FIELD_BINDING_SLOTS = Object.freeze([
  'time', 'comparisons', 'point_mode', 'source_mode', 'relationships', 'context_queries',
]);

/** `bindings.time.range.mode` — `$defs/date_range`, one branch per mode. */
export const WIDGET_DATE_RANGE_MODES = Object.freeze([
  'ALL_TIME', 'CURRENT_PERIOD', 'LAST_N', 'PERIOD_TO_DATE', 'FIXED',
]);

/** A comparison's window, and how its result is expressed. */
export const WIDGET_COMPARISON_MODES = Object.freeze([
  'PREVIOUS_PERIOD', 'SAME_PERIOD_PREVIOUS_YEAR', 'PERIOD_TO_DATE',
]);
export const WIDGET_COMPARE_AS = Object.freeze(['DELTA', 'RATIO', 'SHARE']);

/** `$defs/format.style` — how the renderer prints the value. */
export const WIDGET_FORMAT_STYLES = Object.freeze([
  'NUMBER', 'INTEGER', 'DECIMAL', 'CURRENCY', 'PERCENT', 'DURATION',
  'DATE', 'DATETIME', 'TEXT', 'BOOLEAN',
]);

/**
 * The keys a binding item may carry, by concern.
 *
 * `additionalProperties: false` on every item, so anything outside the union
 * of these and the slot's own extras (`align`, `width_px`, `mode`…) is a
 * rejected payload rather than an ignored key.
 */
export const BINDING_ITEM_KEYS = Object.freeze({
  /** Which data. */
  source: Object.freeze(['field', 'field_key']),
  /** What is done to it before it is read. */
  operation: Object.freeze(['aggregate', 'time_grain', 'date_part']),
  /** How it reads once it is there. */
  presentation: Object.freeze(['label', 'format', 'is_visible']),
});

/**
 * What the engine accepts as an alias, and so as a `field_key`.
 *
 * The schema states it on `TABLE.columns[].key` and nowhere else; guide 18
 * §3.4 is explicit that a wider length must not be inferred from its absence
 * on the other slots. `widgetFieldKey` is the only module that applies it.
 */
export const FIELD_KEY_PATTERN = /^[a-z][a-z0-9_]{0,59}$/;
export const FIELD_KEY_MAX_LENGTH = 60;
